import { ChangeEvent, forwardRef } from "react";
import {
  Input as InputPrimitive,
  InputProps as InputPrimitiveProps,
  TextFieldProps as TextFieldPrimitiveProps,
} from "react-aria-components";

import { getBorderRadiusProps } from "@/components/BorderRadius/BorderRadius";
import { getFocusOutlineProps } from "@/components/FocusOutline/FocusOutline";
import { InputBorder } from "@/components/InputBorder/InputBorder";
import { getInputBorderState } from "@/components/InputBorder/InputBorder.utils";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { SVGCrossCircle } from "@/components/SVGIcon/static/SVGCrossCircle";
import { mergeProps } from "@/utils/reactExtensions";

import styles from "./TextInput.module.scss";
import { Stack } from "../Stack/Stack";

export type TextInputProps = Omit<InputPrimitiveProps, "onChange"> & {
  /**
   * Whether the value is invalid.
   */
  isInvalid?: TextFieldPrimitiveProps["isInvalid"];

  /**
   * Whether the input is disabled.
   */
  isDisabled?: TextFieldPrimitiveProps["isDisabled"];

  /**
   * Whether the input is loading.
   */
  isLoading?: boolean;

  /**
   * Event handler for input change.
   */
  onChange?: TextFieldPrimitiveProps["onChange"];

  /**
   * Prefix icon to display before the input.
   */
  prefixIcon?: React.ReactNode;

  /**
   * Whether the input is clearable (show Clear button).
   *
   * Note: Provide aria label for clear icon using `clearIconAriaLabel`
   * and event handler for clear input using `onClearInput`.
   */
  clearable?: boolean;

  /**
   * Event handler for clearing the input. Inactive when `clearable` is `false`.
   */
  onClearInput?: () => void;

  /**
   * Aria label for the clear icon.
   */
  clearIconAriaLabel?: string;
};

const TextInput = /*#__PURE__*/ (function () {
  const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
    (
      {
        isInvalid,
        isDisabled,
        isLoading,
        clearable,
        onClearInput,
        onChange,
        value,
        prefixIcon,
        clearIconAriaLabel = "Clear input",
        ...rest
      },
      ref
    ) => {
      const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
          onChange(event.target.value);
        }
      };
      const isDefault = isInvalid === undefined;

      return (
        <InputBorder
          aria-invalid={isInvalid ? "true" : "false"}
          aria-describedby={isInvalid === false ? "valid" : ""}
          isHoverStateDisabled={isDisabled || !isDefault}
          state={getInputBorderState({ isInvalid, isDefault })}
        >
          <Stack
            spacing="3xs"
            {...mergeProps(
              getFocusOutlineProps({ within: true }),
              getBorderRadiusProps("extra-medium"),
              {
                className: styles.inputContainer,
              }
            )}
            aria-describedby={isInvalid === false ? "valid" : ""}
          >
            {prefixIcon && <div className={styles.iconContainer}>{prefixIcon}</div>}
            <InputPrimitive
              onChange={handleChange}
              value={value}
              ref={ref}
              {...mergeProps(rest, getBorderRadiusProps("small"), {
                className: styles.input,
              })}
              aria-invalid={isInvalid ? "true" : "false"}
            />

            {isLoading && <LoadingSpinner />}

            {clearable && (
              <button
                type="button"
                onClick={onClearInput}
                aria-label={clearIconAriaLabel}
                // Keyboard users can clear the input using delete or backspace keys (combined with CMD/CTRL)
                tabIndex={-1}
                {...mergeProps(getFocusOutlineProps(), getBorderRadiusProps("rounded"))}
              >
                <SVGCrossCircle width={24} height={24} className={styles.clearIcon} />
              </button>
            )}
          </Stack>
        </InputBorder>
      );
    }
  );

  TextInput.displayName = "TextInput";
  return TextInput;
})();

export { TextInput };
